import { useState } from "react";
import { useNavigate } from "react-router-dom";
import BackButton from "../../../components/Button/BackButton";
import Button from "../../../components/Button/Button";
import FormInput from "../../../components/Form/FormInput";
import PasswordStrengthMeter from "../../../components/Auth/PasswordStrengthMeter";
import api from "../../../utils/axiosInstance";
import { isPasswordCompliant } from "../../../utils/passwordPolicy";

// The checklist beside the field spells out which rule is unmet, so the inline
// error stays one line and the layout never shifts.
const NEW_PASSWORD_ERROR = "Does not meet the requirements listed alongside.";

const ChangePassword = () => {
  const [form, setForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const validate = () => {
    const currentErrors = {};
    if (!form.currentPassword)
      currentErrors.currentPassword = "Current password is required.";
    if (!form.newPassword) {
      currentErrors.newPassword = "New password is required.";
    } else if (!isPasswordCompliant(form.newPassword)) {
      currentErrors.newPassword = NEW_PASSWORD_ERROR;
    }
    if (!form.confirmNewPassword)
      currentErrors.confirmNewPassword = "Please confirm your new password.";
    if (
      form.newPassword &&
      form.confirmNewPassword &&
      form.newPassword !== form.confirmNewPassword
    ) {
      currentErrors.confirmNewPassword = "Passwords do not match.";
    }
    setErrors(currentErrors);
    return Object.keys(currentErrors).length === 0;
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
    setServerError("");
    setSuccess("");
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let errorMsg = "";

    if (name === "newPassword") {
      if (!value) errorMsg = "New password is required.";
      else if (!isPasswordCompliant(value)) errorMsg = NEW_PASSWORD_ERROR;
    } else if (!value) {
      if (name === "currentPassword") errorMsg = "Current password is required.";
      if (name === "confirmNewPassword")
        errorMsg = "Please confirm your new password.";
    }

    if (
      name === "confirmNewPassword" &&
      form.newPassword &&
      value &&
      form.newPassword !== value
    ) {
      errorMsg = "Passwords do not match.";
    }

    setErrors((prev) => ({ ...prev, [name]: errorMsg }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);
      const res = await api.post(
        "/auth/change-password",
        {
          currentPassword: form.currentPassword,
          newPassword: form.newPassword,
        },
        { withCredentials: true }
      );

      setSuccess(res.data.message);
      setForm({ currentPassword: "", newPassword: "", confirmNewPassword: "" });

      setTimeout(() => {
        navigate("/profile");
      }, 1000);
    } catch (err) {
      setServerError(err.response?.data?.error || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-5 flex items-center gap-3">
        <BackButton />
        <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">
          Change Password
        </h1>
      </div>

      <form
        onSubmit={handleSubmit}
        noValidate
        className="max-w-3xl space-y-5 rounded-lg border border-gray-100 bg-white p-5 shadow-sm"
      >
        {serverError && (
          <div className="rounded border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">
            {serverError}
          </div>
        )}
        {success && (
          <div className="rounded border border-success-200 bg-success-50 px-3 py-2 text-sm text-success-800">
            {success}
          </div>
        )}

        {/* Fields on the left, the live requirements alongside them, so the
            whole form stays within one screen as the checklist fills in. */}
        <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-2">
          <div className="space-y-4">
            <FormInput
              label="Current Password"
              name="currentPassword"
              type="password"
              placeholder="Current password"
              value={form.currentPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.currentPassword}
              required
            />
            <FormInput
              label="New Password"
              name="newPassword"
              type="password"
              placeholder="New password"
              value={form.newPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.newPassword}
              required
            />
            <FormInput
              label="Confirm New Password"
              name="confirmNewPassword"
              type="password"
              placeholder="Re-enter new password"
              value={form.confirmNewPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.confirmNewPassword}
              required
            />
          </div>

          <div className="rounded-lg border border-gray-100 bg-gray-50/70 p-4">
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
              Password Requirements
            </h2>
            <PasswordStrengthMeter password={form.newPassword} alwaysVisible />
            <p className="mt-3 border-t border-gray-200 pt-3 text-[11px] text-gray-500">
              You cannot reuse any of your last 5 passwords.
            </p>
          </div>
        </div>

        <div className="space-y-3 border-t border-gray-100 pt-3">
          <p className="text-xs text-gray-500">
            Fields marked with <span className="text-danger-500">*</span> are mandatory.
          </p>
          <div className="flex gap-2">
            <Button size="sm" type="submit" disabled={loading}>
              {loading ? "Updating..." : "Change Password"}
            </Button>
            <Button size="sm"
              type="button"
              variant="secondary"
              onClick={() => navigate("/profile")}
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ChangePassword;
